import { RangeAreaDimension, RangeDoubleSizeDimension, SinglePlusDimension, SizeAndQtyDimension, TotalSizeDimension } from "../Dimensions";
import { DimensionData } from "utils";
import useAdBlockStore from "store/AdBlockStore";
import { Checkbox, Button } from "antd";
import { getDefaultProductSizeApi, getProductBrandAutocompleteApiV2 } from "api";
import { useState, useCallback, useEffect, useRef } from "react";
import { LabelledAutocompleteBrand, LabelledSelect } from "../Fields";
import useKeypress from "react-use-keypress";

const DimensionEntry = ({ dimension, value }) => {
  switch (dimension) {
    case 1: // Single
    case 3: // Plus
      return <SinglePlusDimension value={value} />;
    case 2: // Range
    case 4: // Area
      return <RangeAreaDimension value={value} />;
    case 9: // AreaRange
    case 5: // SizeAndQty
      return <SizeAndQtyDimension value={value} />;
    case 7: // DoubleSize
    case 6: // RangeDoubleSize
      return <RangeDoubleSizeDimension value={value} />;
    case 26: // TotalSize
      return <TotalSizeDimension value={value} />;
    default:
      return null;
  }
};


export const ProductBuilder = () => {
  const currentProduct = useAdBlockStore((state) => state.currentProduct);
  const updateCurrentProduct = useAdBlockStore((state) => state.updateCurrentProduct);
  const setActiveField = useAdBlockStore((state) => state.setActiveField);
  const focusCurrentField = useAdBlockStore((state) => state.focusCurrentField);
  const addBrandToCurrentProduct = useAdBlockStore((state) => state.addBrandToCurrentProduct);
  const focusPreviousField = useAdBlockStore((state) => state.focusPreviousField);
  const focusNextField = useAdBlockStore((state) => state.focusNextField);
  const products = useAdBlockStore((state) => state.products);
  const currentRetailer = useAdBlockStore((state) => state.retailerID);

  const [brandOptions, setBrandOptions] = useState([]);
  const [brandInputValue, setBrandInputValue] = useState("");
  const latestBrandSearch = useRef("");
  const [isDefaultSizeLoading, setIsDefaultSizeLoading] = useState(false);
  const currentAdBlockID = useAdBlockStore((state) => state.currentAdBlockID);

  // Reset brandInputValue and toAddBrand when adblock changes
  useEffect(() => {
    setBrandInputValue("");
    setBrandOptions([]);
    useAdBlockStore.setState({ toAddBrand: "" });
  }, [currentAdBlockID]);

  useKeypress(['Tab', 'Enter'], (event) => {
    if (event.shiftKey) {
      focusPreviousField("product-builder", event);
      return;
    }
    focusNextField("product-builder", event);
  });

  useKeypress([']', '['], (event) => {
    // remove focus
    event.preventDefault();
    document.activeElement.blur();
  })

  // designate keypress for jumping to brand field if product builder is focused
  useKeypress(['/'], (event) => {
    if (event.altKey || event.metaKey) {
      event.preventDefault();
      const brandField = document.querySelector('#brand-autocomplete');
      if (brandField) {
        brandField.focus();
      }
    }
  });

  // Debounce function
  const debounce = useCallback((func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
  }, []);

  // Search brands function
  const searchBrands = async (searchText) => {
    if (!searchText || searchText.length < 2) {
      setBrandOptions([]);
      return;
    }
    try {
      const response = await getProductBrandAutocompleteApiV2(searchText, currentRetailer);
      // Drop results for text the user has since changed, so Enter can't pick a stale brand
      if (searchText !== latestBrandSearch.current) return;
      if (response.success) {
        const options = response.results.map(brand => ({
          value: brand.name,
          label: brand.name,
          active_f: brand.active_f,
          brand: brand
        }));
        setBrandOptions(options);
      }
    } catch (error) {
      setBrandOptions([]);
    }
  };

  // Debounced search function
  const debouncedSearchBrands = useCallback(debounce(searchBrands, 300), [debounce, currentRetailer]);

  const handleBrandSelect = async (value, option) => {
    addBrandToCurrentProduct({ brand_uuid: option.brand.uuid });
    // Auto-advance to next field after selection
    setTimeout(() => focusNextField("product-builder"), 0);
  };

  const isDefaultSizeDisabled =
    !currentProduct?.ext_brand_i ||
    !currentProduct?.brand_nm ||
    isDefaultSizeLoading;

  const handleDefaultSizeClick = async () => {
    const sub_cat_i = currentProduct?.sub_cat_i || currentProduct?.brand?.sub_cat_i;
    const product_nm = currentProduct?.brand_nm || currentProduct?.brand?.brand_name;

    if (!sub_cat_i || !product_nm) {
      return;
    }

    try {
      setIsDefaultSizeLoading(true);
      const response = await getDefaultProductSizeApi(sub_cat_i, product_nm);
      if (!response.success) {
        return;
      }

      const payload = response.payload || {};
      const size_unit_i = payload.size_unit_i;
      const size_nbr = payload.size_nbr;

      if (size_unit_i === undefined && size_nbr === undefined) {
        return;
      }

      const updates = {};
      if (size_unit_i !== undefined && size_unit_i !== null) {
        updates.ext_size_unit_i = size_unit_i;
      }
      if (size_nbr !== undefined && size_nbr !== null) {
        updates.size_1 = size_nbr;
      }

      if (Object.keys(updates).length > 0) {
        updateCurrentProduct(updates);
      }
    } catch (error) {
    } finally {
      setIsDefaultSizeLoading(false);
    }
  };

  if (!products.length > 0) {
    return <>No products available.</>;
  }

  return (
    <span id="product-builder" className="product-builder">
      <LabelledAutocompleteBrand
        id="brand-autocomplete"
        value={currentProduct?.brand?.brand_name || brandInputValue}
        label={"Brand Name"}
        onSearch={(value) => {
          setBrandInputValue(value);
          // Clear old results while typing so Enter can't select a brand from a previous search
          setBrandOptions([]);
          latestBrandSearch.current = value;
          debouncedSearchBrands(value);
        }}
        style={{ width: "100%", marginBottom: "8px" }}
        placeholder="Search brands"
        onSelect={handleBrandSelect}
        options={brandOptions}
        filterOption={false}
        onFocus={() => {
          setActiveField("brand");
          // focusCurrentField("product-builder")
        }}
        allowClear
        addable
        onAddNew={() => useAdBlockStore.setState({ leftPanelView: "brand", toAddBrand: brandInputValue })}
      />
      {currentProduct?.brand?.variants && currentProduct?.brand?.variants?.length > 0 && (
        <LabelledSelect
          mode="multiple"
          label="Brand Variant"
          placeholder="Variety (Optional)"
          variant="filled"
          allowClear
          options={currentProduct.brand.variants.map(item => ({
            label: item.variety_nm,
            value: item.ext_id,
          }))}
          value={currentProduct?.variant_ids || []}
          onChange={(value) => {
            updateCurrentProduct({ variant_ids: value || [], variant_nms: value.map(v => currentProduct.brand.variants.find(variant => variant.ext_id === v)?.variety_nm || "") });
            setTimeout(() => focusNextField("product-builder"), 0);
          }}
          onFocus={() => setActiveField("variant")}
          addable
          onAddNew={() => useAdBlockStore.setState({ leftPanelView: "variety" })}
        />
      )}

      {currentProduct?.brand?.origins && currentProduct?.brand?.origins.length > 0 && (
        <LabelledSelect
          mode="multiple"
          label="Brand Origin"
          placeholder="Origin (Optional)"
          variant="filled"
          allowClear
          options={currentProduct.brand.origins.map(item => ({
            label: item.origin_nm,
            value: item.ext_id,
          }))}
          value={currentProduct?.origin_ids || []}
          onChange={(value) => {
            updateCurrentProduct({ origin_ids: value || [], origin_nms: value.map(v => currentProduct.brand.origins.find(origin => origin.ext_id === v)?.origin_nm || "") });
            setTimeout(() => focusNextField("product-builder"), 0);
          }}
          onFocus={() => setActiveField("origin")}
          addable
          onAddNew={() => useAdBlockStore.setState({ leftPanelView: "origin" })}
        />
      )}

      {currentProduct?.brand?.model_numbers && currentProduct?.brand?.model_numbers.length > 0 && (
        <LabelledSelect
          mode="multiple"
          type={"inline"}
          label="Model Number"
          placeholder="Select model numbers..."
          variant="filled"
          allowClear
          options={currentProduct.brand.model_numbers.map(item => ({
            label: item.model_number,
            value: item.ext_id,
          }))}
          value={currentProduct?.model_number_ids || []}
          onChange={(value) => {
            updateCurrentProduct({ model_number_ids: value || [], model_number_nms: value.map(v => currentProduct.brand.model_numbers.find(model => model.ext_id === v)?.model_number || "") });
            setTimeout(() => focusNextField("product-builder"), 0);
          }}
          onFocus={() => setActiveField("model_number")}
          addable
          onAddNew={() => useAdBlockStore.setState({ leftPanelView: "model_number" })}
        />
      )}

      <LabelledSelect
        type={"inline"}
        label="Unit Dimension"
        placeholder="Select product type..."
        variant="filled"
        allowClear
        options={
          DimensionData.map(item => ({
            label: item.title,
            value: item.id,
          }))
        }
        value={DimensionData.find(item => item.id === currentProduct?.size_dimension_i)?.title || ""}
        onChange={(value) => {
          updateCurrentProduct({ size_dimension_i: value });
          setTimeout(() => focusNextField("product-builder"), 0);
        }}
        onFocus={() => setActiveField("size_dimension_i")}
      />
      <div className="mt-2 mb-2">
        <Button
          size="medium"
          onClick={handleDefaultSizeClick}
          disabled={isDefaultSizeDisabled}
          loading={isDefaultSizeLoading}
        >
          Default Size
        </Button>
      </div>
      <DimensionEntry
        dimension={currentProduct?.size_dimension_i}
        value={currentProduct?.size_dimension_value}
      />

      <Checkbox
        checked={currentProduct?.liner_f}
        onChange={(e) => updateCurrentProduct({ liner_f: e.target.checked })}
        onFocus={() => setActiveField("none")}
      >
        Is this product a liner?
      </Checkbox>
    </span>
  )
}